import { usePortalData } from "../context/PortalDataContext";

const HEALTH_TIPS = [
  "Take medications at the same time each day for best results.",
  "Drink at least 8 glasses of water daily.",
  "Store medicines away from heat and direct sunlight.",
  "Never stop a prescribed course of antibiotics early.",
  "Keep an updated list of all your medications with you.",
  "Check expiry dates on medicines regularly.",
  "Never share prescription medicines with others.",
];

const STATUS_COLORS = {
  pending:   { bg: "#fef3c7", color: "#92400e" },
  processed: { bg: "#ecfdf5", color: "#065f46" },
  rejected:  { bg: "#fef2f2", color: "#991b1b" },
};

function formatRelativeDate(iso) {
  if (!iso) return "";
  const date     = new Date(iso);
  const today    = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.floor((today - date) / 86400000);
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7)  return `${diffDays}d ago`;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

const SkeletonLine = ({ w }) => (
  <div style={{
    height: 11, width: w, borderRadius: 4,
    background: "var(--border-light)",
    animation: "pulse 1.4s ease-in-out infinite",
  }} />
);

export default function PortalRightPanel() {
  const {
    activePrescriptionCount,
    pendingRefillCount,
    totalPrescriptionCount,
    pharmacyCount,
    selectedPharmacyName,
    selectedDepartmentName,
    recentRefills,
    loading,
  } = usePortalData();

  const tip = HEALTH_TIPS[new Date().getDate() % HEALTH_TIPS.length];

  return (
    <>
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:.45}}`}</style>

      <div className="portal-right">

        {/* ── My Pharmacy ─────────────────────────────────────────── */}
        <div className="panel-widget">
          <div className="panel-widget-header">
            <i className="pi pi-building" />
            My Pharmacy
          </div>
          <div className="panel-widget-body">
            {!selectedPharmacyName ? (
              <div style={{ fontSize: 12.5, color: "var(--text-muted)", lineHeight: 1.6 }}>
                Select a pharmacy from the top bar to get started.
              </div>
            ) : (
              <>
                <div className="pharmacy-info-name">{selectedPharmacyName}</div>
                {selectedDepartmentName && (
                  <div className="pharmacy-info-address">
                    <i className="pi pi-map-marker" style={{ fontSize: 10, marginRight: 4 }} />
                    {selectedDepartmentName}
                  </div>
                )}
                <div className="pharmacy-info-badge">
                  <i className="pi pi-check-circle" style={{ fontSize: 11 }} />
                  Connected
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── My Summary ──────────────────────────────────────────── */}
        <div className="panel-widget">
          <div className="panel-widget-header">
            <i className="pi pi-chart-bar" />
            My Summary
          </div>
          <div className="panel-widget-body">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {[
                {
                  label: "Active Rx",
                  value: activePrescriptionCount,
                  icon: "pi-file-edit",
                  bg: "#dbeafe", iconColor: "#1d4ed8",
                },
                {
                  label: "Pending Refills",
                  value: pendingRefillCount,
                  icon: "pi-refresh",
                  bg: "#fef3c7", iconColor: "#b45309",
                },
                {
                  label: "Total Rx",
                  value: totalPrescriptionCount,
                  icon: "pi-calendar",
                  bg: "#ecfdf5", iconColor: "#059669",
                },
                {
                  label: "Pharmacies",
                  value: pharmacyCount,
                  icon: "pi-building",
                  bg: "#ede9fe", iconColor: "#6d28d9",
                },
              ].map((stat) => (
                <div key={stat.label} style={{
                  background: stat.bg, borderRadius: 10,
                  padding: "10px 10px", display: "flex",
                  flexDirection: "column", gap: 5,
                }}>
                  <i className={`pi ${stat.icon}`} style={{ fontSize: 13, color: stat.iconColor }} />
                  {loading ? (
                    <SkeletonLine w={28} />
                  ) : (
                    <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text-primary)", lineHeight: 1 }}>
                      {stat.value}
                    </div>
                  )}
                  <div style={{ fontSize: 10.5, color: "var(--text-secondary)", lineHeight: 1.3 }}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Recent Refill Requests ───────────────────────────────── */}
        <div className="panel-widget">
          <div className="panel-widget-header">
            <i className="pi pi-refresh" />
            Recent Refills
          </div>
          <div className="panel-widget-body" style={{ padding: "6px 14px 10px" }}>
            {loading ? (
              [1, 2, 3].map((i) => (
                <div key={i} style={{ display: "flex", gap: 8, padding: "7px 0", borderBottom: "1px solid var(--border-light)" }}>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--border-color)", marginTop: 4, flexShrink: 0 }} />
                  <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: 1 }}>
                    <SkeletonLine w="80%" />
                    <SkeletonLine w="40%" />
                  </div>
                </div>
              ))
            ) : recentRefills.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--text-muted)", padding: "8px 0" }}>
                No refill requests yet.
              </div>
            ) : (
              recentRefills.map((r, i) => {
                const sc     = STATUS_COLORS[r.status] ?? STATUS_COLORS.pending;
                const label  = r.items?.[0]?.product
                  ?? `${r.items?.length ?? 0} medicines`;
                const isLast = i === recentRefills.length - 1;

                return (
                  <div key={r.id} style={{
                    display: "flex", gap: 8, alignItems: "flex-start",
                    padding: "8px 0",
                    borderBottom: isLast ? "none" : "1px solid var(--border-light)",
                  }}>
                    <div style={{
                      width: 6, height: 6, borderRadius: "50%",
                      background: sc.color, marginTop: 4, flexShrink: 0,
                    }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 12, color: "var(--text-primary)",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      }}>
                        {label}
                        {r.items?.length > 1 && (
                          <span style={{ color: "var(--text-muted)", marginLeft: 4 }}>
                            +{r.items.length - 1}
                          </span>
                        )}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 3 }}>
                        <span style={{
                          fontSize: 10, fontWeight: 600,
                          background: sc.bg, color: sc.color,
                          padding: "1px 6px", borderRadius: 20,
                        }}>
                          {r.status}
                        </span>
                        <span style={{ fontSize: 10.5, color: "var(--text-muted)" }}>
                          {formatRelativeDate(r.date)}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* ── Health Tip ───────────────────────────────────────────── */}
        <div className="panel-widget">
          <div className="panel-widget-header">
            <i className="pi pi-heart" />
            Health Tip
          </div>
          <div className="panel-widget-body">
            <div style={{
              background: "#f0fdf4",
              borderRadius: 8,
              padding: "10px 12px",
              borderLeft: "3px solid #16a34a",
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
            }}>
              <p style={{ margin: 0, fontSize: 12.5, color: "#166534", lineHeight: 1.6 }}>
                {tip}
              </p>
            </div>
          </div>
        </div>

      </div>
    </>
  );
}
